import { RawTemplate, TemplateEntity } from './Types';
import { FragmentEntity, RawFragment } from '../Fragment/Types';
import { FragmentModel } from '../Fragment/FragmentModel';
import { QuestionEntity, RawQuestion } from '../Question/Types';
import { QuestionModel } from '../Question/QuestionModel';
import { ItemTypeEntity } from '../ItemType/Types';
import { BaseModel } from '../BaseModel/BaseModel';
import { MapQuestionsToIndexedObject } from '@modules/Steps/Shared/Helpers/MapQuestionsToIndexedObject';

import { CategoryEntity } from '../Category/Types';
import { CategoryModel } from '../Category/CategoryModel';
import { ExtractQuestions } from '@modules/Steps/Shared/Enums/ExtractQuestions';
import { TemplateForUpdatePayload } from '../../Processes/Gateways/CustomProjects/Interfaces/TemplateForUpdatePayload';
import { ItemTypeModel } from '../ItemType/ItemTypes';
import { GalleryEntity } from '../Gallery/Types';
import { GalleryModel } from '../Gallery/GalleryModel';

export class TemplateModel
    extends BaseModel<RawTemplate, TemplateEntity>
    implements TemplateEntity
{
    id: number = 0;
    title: string = '';
    createdAt: string = new Date().toISOString();
    updatedAt: string = new Date().toISOString();
    deletedAt: string | null = null;
    description?: string | null;
    shortDescription: string | null = null;
    bought: boolean = false;
    paid: boolean = false;
    publish?: boolean;
    reviews: number = 0;
    frozen: boolean = false;
    counted?: boolean | null;
    fragments: FragmentEntity[] = [];
    questions: QuestionEntity[] = [];
    summaryItemTypesTemplate: ItemTypeEntity[] = [];
    categories?: CategoryEntity[] = [];
    galleries?: GalleryEntity[] = [];
    slug: string = '';
    templateGroupId: number | null = null;

    constructor(template?: RawTemplate) {
        super();
        if (template) {
            this.initialize(template);
        }
    }

    initialize(template: RawTemplate) {
        this.id = template.id;
        this.title = template.title;
        this.createdAt = template.created_at;
        this.updatedAt = template.updated_at;
        this.description = template.description ?? '';
        this.shortDescription = template.short_description ?? '';
        this.bought = template.bought;
        this.paid = template.paid;
        this.publish = template.publish ?? false;
        this.reviews = template.reviews;
        this.frozen = template.frozen;
        this.deletedAt = template.deleted_at;
        this.counted = template.counted;
        this.slug = template.slug;
        this.templateGroupId = template.template_group_id;

        this.fragments = template.data.fragments.map(
            (fragment: RawFragment) => new FragmentModel(fragment)
        );
        this.questions = ExtractQuestions(template.data).map(
            ([_, value]: [string, RawQuestion]) => new QuestionModel(value)
        );

        this.summaryItemTypesTemplate =
            template.data.summary_item_types_template.map(
                data => new ItemTypeModel(data)
            );
        this.categories = template.categories
            ? template.categories.map(category => new CategoryModel(category))
            : [];

        this.galleries = template.galleries?.length
            ? template.galleries.map(gallery => new GalleryModel(gallery))
            : [];
    }

    parse(): RawTemplate {
        return {
            id: this.id,
            title: this.title,
            created_at: this.createdAt,
            updated_at: this.updatedAt,
            deleted_at: this.deletedAt,
            description: this.description,
            short_description: this.shortDescription,
            bought: this.bought,
            paid: this.paid,
            publish: this.publish,
            reviews: this.reviews,
            frozen: this.frozen,
            slug: this.slug,
            template_group_id: this.templateGroupId,
            data: {
                fragments: this.fragments.map(fragment => fragment.parse()),
                ...MapQuestionsToIndexedObject(this.questions),
                summary_item_types_template: this.summaryItemTypesTemplate.map(
                    item => item.parse()
                ),
            },
            categories: this.categories
                ? this.categories.map(category => category.parse())
                : [],
            ...(this.counted !== undefined && { counted: this.counted }),
            ...(this.galleries && {
                galleries: this.galleries.map(gallery => gallery.parse()),
            }),
        };
    }

    transformTemplateForUpdate(): TemplateForUpdatePayload {
        return {
            id: this.id,
            questions_from_template: this.questions.map(question =>
                question.transformQuestionForUpdate()
            ),
            questions_from_fragments: this.fragments.map(fragment =>
                fragment.transformFragmentForUpdate()
            ),
            counted: true,
        };
    }
}
